import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Column } from 'primereact/column';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Container } from './styles';

import AtualizacaoService from '../../services/AtualizacaoService';
import AuthService from '../../services/AuthService';
import history from '../../services/history';
import {
  getPageParams,
  errorHandle,
  padLeft,
  calcNaxItemsPage,
  isScreenMobile,
} from '../../util/functions';

import { showMessage } from '../../components/MessageDialog';

import BotaoMenuGrid from '../../components/BotaoMenuGrid';

import AtualizacaoCrud from './crud';
import { StateScreen } from '../constants';
import CrudHeader from '../../components/CrudHeader';
import DataTableSp from '../../components/DataTableSp';

// Central de atualizações: catálogo de versões publicadas do pacote Speedy.
// Publicar NÃO libera ninguém — a liberação é pelos rollouts (dentro do registro).
export default function Atualizacao(props) {
  const filterVisible = useSelector(state => state.global.filterVisible);

  const pageParams = useMemo(() => getPageParams(props.match.params, props.location.search), [
    props.location.search,
    props.match.params,
  ]);

  const filterService = useMemo(() => AtualizacaoService.getFilter(), []);

  const toBack = pageParams.toBack || '/atualizacoes';

  const [filter, setFilter] = useState(filterService);
  const [atualizacoes, setAtualizacoes] = useState([]);

  const [pageLimit, setPageLimit] = useState(filterService.limit);
  const [first, setFirst] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSelected, setPageSelected] = useState(0);

  const calcLimit = useCallback(() => {
    let ret;
    if (filterVisible) {
      ret = calcNaxItemsPage(10, 14, 19);
    } else {
      ret = calcNaxItemsPage(13, 17, 22);
    }
    setPageLimit(ret);
    return ret;
  }, [filterVisible]);

  const handleBuscar = useCallback(
    async (_filter, _page, resetPage = true) => {
      _filter.page = _page || 0;
      _filter.limit = calcLimit();
      try {
        const result = await AtualizacaoService.findAll(_filter);
        setAtualizacoes(result.items);

        setTotalRecords(result.totalRecords);
        if (resetPage) {
          setFirst(0);
        }
      } catch (err) {
        errorHandle(err);
      }
    },
    [calcLimit]
  );

  function onPage(event) {
    const pagina = event.first / event.rows;
    setPageSelected(pagina);
    setFirst(event.first);
    handleBuscar(filter, pagina, false);
  }

  const excluirRegistro = useCallback(
    async _id => {
      try {
        await AtualizacaoService.delete(_id);
        toast.success('Versão excluída com sucesso.');
        handleBuscar(filter);
      } catch (err) {
        errorHandle(err);
      }
    },
    [filter, handleBuscar]
  );

  function getTitle() {
    const titleDefault = 'Atualizações';
    let titleAdd = '';

    if (pageParams.stateScreen === StateScreen.stSearch) {
      titleAdd = '';
    }
    if (pageParams.stateScreen === StateScreen.stInsert) {
      titleAdd = '(Publicar Versão)';
    }
    if (pageParams.stateScreen === StateScreen.stUpdate) {
      titleAdd = `(Alterando Id: ${pageParams.idSelected})`;
    }
    if (pageParams.stateScreen === StateScreen.stView) {
      titleAdd = ` (Visualizando Id: ${pageParams.idSelected})`;
    }

    if (!isScreenMobile()) {
      return `${titleDefault} ${titleAdd}`;
    }

    return titleDefault;
  }

  const confirmaExclusao = useCallback(
    id => {
      showMessage(
        'Confirmação',
        'Excluir a versão remove o pacote e os rollouts dela. Confirma?',
        idx => {
          if (idx === 1) {
            excluirRegistro(id);
          }
        }
      );
    },
    [excluirRegistro]
  );

  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      handleBuscar(filter, 0, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      calcLimit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcLimit]);

  return (
    <Container className="container-page page-enter-tab">
      <div className="p-grid">
        <CrudHeader
          title={getTitle()}
          disabledButtonInsert={!AuthService.checkRoles('ATUALIZACAO_INSERIR')}
          showButtonFilter={pageParams.stateScreen === StateScreen.stSearch}
          showButtonInsert
          showButtonSearch
          showButtonConfig={false}
          titleFilterDefault="Buscar por Versão"
          handleFilterDefault={text => {
            filter.versao = text;
            setFilter({ ...filter, versao: text });
            handleBuscar(filter);
          }}
          handleClearFilters={() => {
            setFilter({
              ...filter,
              versao: '',
            });
          }}
          handleButtonInsert={() => history.push('/atualizacoes/insert')}
          handleButtonSearch={() => handleBuscar(filter)}
        />
        {pageParams.stateScreen === StateScreen.stSearch ? renderSearch() : renderCrud()}
      </div>
    </Container>
  );

  function renderSearch() {
    return (
      <>
        <div className="p-col-12 p-fluid datatable-doc-demo">
          <DataTableSp
            value={atualizacoes}
            style={{ marginBottom: '2px' }}
            paginator
            rows={pageLimit}
            lazy
            responsive
            totalRecords={totalRecords}
            first={first}
            onPage={onPage}
          >
            <Column
              field="id"
              body={rowData => padLeft(rowData.id, 6)}
              header="Id"
              className="grid-col-id"
            />

            <Column field="versao" className="grid-col" style={{ width: 120 }} header="Versão" />

            <Column
              field="tamanho"
              className="grid-col-curr"
              style={{ width: 120 }}
              header="Tamanho"
              body={rowData => `${(Number(rowData.tamanho) / 1024 / 1024).toFixed(1)} MB`}
            />

            <Column
              field="dataCriacao"
              className="grid-col"
              style={{ width: 160 }}
              header="Publicada em"
              body={rowData =>
                rowData.dataCriacao ? new Date(rowData.dataCriacao).toLocaleString('pt-BR') : ''
              }
            />

            <Column
              field="ativo"
              className="grid-col-curr grid-col-center"
              style={{ width: 80 }}
              header="Ativa"
              body={rowData => (rowData.ativo ? 'Sim' : 'Não')}
            />

            <Column field="notas" className="grid-col" header="Notas" />

            <Column
              className="gid-col-acoes-35"
              bodyStyle={{ textAlign: 'end' }}
              body={renderButtonOp}
            />
          </DataTableSp>
        </div>
      </>
    );
  }

  function renderButtonOp(rowData) {
    return (
      <BotaoMenuGrid
        handles={[
          () => history.push(`/atualizacoes/${rowData.id}?view`),
          () => history.push(`/atualizacoes/${rowData.id}`),
          () => confirmaExclusao(rowData.id),
        ]}
        disableds={[
          false,
          !AuthService.checkRoles('ATUALIZACAO_ALTERAR'),
          !AuthService.checkRoles('ATUALIZACAO_EXCLUIR'),
        ]}
      />
    );
  }

  function renderCrud() {
    return (
      <AtualizacaoCrud
        idSelected={pageParams.idSelected}
        stateScreen={pageParams.stateScreen}
        onClose={() => {
          history.push(toBack);
          handleBuscar(filter, pageSelected, false);
        }}
      />
    );
  }
}
